import {Box, LoadingOverlay, Title} from '@mantine/core';
import classNames from 'classnames';
import Head from 'next/head';
import {useRouter} from 'next/router';
import type React from 'react';
import {type FC, useEffect, useState} from 'react';
import {MdBusiness, MdCheckCircle, MdControlPoint} from 'react-icons/md';
import type {z} from 'zod';
import {COLOR_WHITE} from '../../../config/constants/colorCodes';
import {GSSKT_HOST} from '../../../config/constants/hosts';
import {
  COMPANIES,
  FOLLOW_COMPANIES,
  HOME,
} from '../../../config/constants/routes';
import {FollowCompanies} from '../../../core/entities/reports/constants';
import type {CompaniesSchema} from '../../../core/entities/usersMe/schema';
import {
  type GetGroupedSupportCompanies,
  type UsersMeFollowedCompaniesResponseType,
  useGetFollowAdCompanies,
} from '../../../core/repositories/companyRepository';
import {
  useDelectFollowCompany,
  usePostFollowCompany,
} from '../../../core/repositories/mypageRepository';
import {BreadcrumbsComponent} from '../../../shared/components/BreadcrumbsComponent';
import {createBreadCrumbsStructuredData} from '../../../shared/services/createBreadCrumbsStructuredData';
import {useMediaQuery} from '../../../shared/services/useMediaQuery';

type Props = {
  getGroupedSupportCompanies?: GetGroupedSupportCompanies;
  isLoading: boolean;
  userFollowedCompanies?: UsersMeFollowedCompaniesResponseType;
};

export const FollowCompaniesPage: React.FC<Props> = ({
  getGroupedSupportCompanies,
  isLoading,
  userFollowedCompanies,
}) => {
  const router = useRouter();
  const currentUrl = `${GSSKT_HOST}${decodeURI(router.pathname)}`;
  const {isPC} = useMediaQuery();

  const crumbs = [
    {label: FollowCompanies.HOME, href: HOME},
    {label: FollowCompanies.Search, href: COMPANIES},
    {label: FollowCompanies.Follow, href: FOLLOW_COMPANIES},
  ];
  const saveFollowCompany = usePostFollowCompany();
  const deleteFollowCompany = useDelectFollowCompany();
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>([]);
  useEffect(() => {
    if (userFollowedCompanies) {
      setSelectedCompanyIds(
        userFollowedCompanies.companies.map((company) => company.id),
      );
    }
  }, []);
  const handleSelectCompany = (id: number) => {
    if (selectedCompanyIds.includes(id)) {
      // フォロー解除
      deleteFollowCompany.mutate(id, {
        onSuccess() {
          setSelectedCompanyIds(
            selectedCompanyIds.filter((companyId) => companyId !== id),
          );
        },
      });
    } else {
      // フォロー
      saveFollowCompany.mutate(id, {
        onSuccess() {
          setSelectedCompanyIds([...selectedCompanyIds, id]);
        },
      });
    }
  };

  const COMPANY_FOLLOW_PAGE_RECOMMEND_COMPANY_LEN = 3;
  const {data} = useGetFollowAdCompanies({
    queryParams: {limit: COMPANY_FOLLOW_PAGE_RECOMMEND_COMPANY_LEN},
  });
  const adCompanyIds = data?.ok
    ? data.value.companies.map((company) => company.id)
    : [];
  return (
    <>
      <Head>
        <title key="title">
          {isPC ? 'フォロー企業の新着 |' : ''} 外資就活ドットコム
        </title>
        <link rel="canonical" href={currentUrl} key="canonical" />
        <script type="application/ld+json">
          {JSON.stringify(createBreadCrumbsStructuredData(crumbs))}
        </script>
        <meta name="robots" content="noindex" />
      </Head>
      <Box className="w-full max-w-[664px]">
        {isPC ? null : <BreadcrumbsComponent crumbs={crumbs} />}

        <Box>
          <Title
            order={1}
            className="border-t-4 border-primary-1 bg-white p-[11px] text-lg text-primary-1 pc:px-32"
          >
            <div
              className={`items-center flex ${isPC ? 'justify-between' : ''}`}
            >
              {isPC ? null : <MdBusiness className="mr-8" />}企業をフォローする
              {isPC ? (
                <div className="text-black-1 text-sm">
                  フォローした企業の新着情報が届きます
                </div>
              ) : null}
            </div>
          </Title>
        </Box>
        <Box className="mt-[10px] mb-[20px] bg-white px-8 pb-32 pc:px-32 pc:pt-32">
          <Box className="font-roboto font-bold">外資就活ピックアップ</Box>
          {
            <Box className="py-8">
              {
                <Box className="pc:grid pc:grid-cols-3">
                  {data?.ok &&
                    data.value.companies.map((company) => {
                      return (
                        <FollowAdCompanyCard
                          key={company.id}
                          company={company}
                          selectedCompanyIds={selectedCompanyIds}
                          handleSelectCompany={handleSelectCompany}
                        />
                      );
                    })}
                </Box>
              }
            </Box>
          }
          <LoadingOverlay
            visible={isLoading}
            overlayBlur={2}
            overlayColor={COLOR_WHITE}
          />
          {getGroupedSupportCompanies?.companyGroups.map((companyGroup) => {
            return (
              <Box className="pt-24" key={companyGroup.industryCategoryID}>
                <Box className="font-roboto font-bold">
                  {companyGroup.industryCategoryName}
                </Box>
                <Box className="grid grid-cols-2 pc:grid-cols-3">
                  {companyGroup.companies
                    .filter((company) => !adCompanyIds.includes(company.id)) // 広告企業は除外
                    .map((company) => {
                      return (
                        <FollowCompanyCard
                          key={company.id}
                          company={company}
                          selectedCompanyIds={selectedCompanyIds}
                          handleSelectCompany={handleSelectCompany}
                        />
                      );
                    })}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </>
  );
};

// ピックアップ用コンポーネント
type FollowAdCompanyCardProps = {
  company: z.infer<typeof CompaniesSchema>;
  selectedCompanyIds: number[];
  handleSelectCompany: (id: number) => void;
};

const FollowAdCompanyCard: FC<FollowAdCompanyCardProps> = ({
  company,
  selectedCompanyIds,
  handleSelectCompany,
}) => {
  const isSelected = selectedCompanyIds.includes(company.id);
  const {isPC} = useMediaQuery();

  return (
    <Box
      className={classNames('rounded-[8px] m-4 hover:cursor-pointer', {
        'border-primary-1 border-2': isSelected,
        'border-line border': !isSelected,
        '': isPC,
      })}
      onClick={() => {
        handleSelectCompany(company.id);
      }}
    >
      <Box
        style={{
          backgroundImage: `url(${
            company.backgroundImageURLs['640x268'] ?? ''
          })`,
          backgroundSize: 'cover',
        }}
        className="items-center flex justify-center p-4 rounded-t-[8px]"
      >
        {company.logoURLs['140x105'] ? (
          <Box
            className="rounded-full border-line border"
            style={{
              backgroundImage: `url(${company.logoURLs['140x105'] ?? ''})`,
              backgroundPosition: '50% 50%',
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundColor: '#fff',
              width: '50px',
              height: '50px',
            }}
          />
        ) : (
          <MdBusiness
            size={40}
            className="text-primary rounded-full border border-line-1"
          />
        )}
      </Box>
      <Box
        className={classNames('rounded-b-[4px] px-4', {
          'bg-primary-1 text-white': isSelected,
          'text-black': !isSelected,
        })}
      >
        <Box className="flex h-40">
          <Box className="grow flex justify-center items-center">
            <span
              className={classNames('line-clamp-2', {
                'text-base': isPC,
                'text-md': !isPC,
              })}
            >
              <a href={`/company/${company.id}`} className="hover:underline">
                {company.name}
              </a>
            </span>
          </Box>
          <Box className="w-20 flex justify-center items-center">
            {isSelected ? (
              <MdCheckCircle size={20} className="bg-primary-1 text-white" />
            ) : (
              <MdControlPoint size={20} className="text-primary" />
            )}
          </Box>
        </Box>
        <Box className="text-xs text-line mx-8 whitespace-nowrap">
          {company.industry}
        </Box>
      </Box>
    </Box>
  );
};

type FollowCompanyCardProps = {
  company: {
    id: number;
    name: string;
    isFollowed: boolean;
  };
  selectedCompanyIds: number[];
  handleSelectCompany: (id: number) => void;
};

const FollowCompanyCard: FC<FollowCompanyCardProps> = ({
  company,
  selectedCompanyIds,
  handleSelectCompany,
}) => {
  const isSelected = selectedCompanyIds.includes(company.id);

  return (
    <Box
      className={classNames(
        'p-4 rounded-[8px] m-4 flex hover:cursor-pointer min-h-[62px]',
        {
          'bg-primary-1 text-white': isSelected,
          'bg-white text-black border border-line': !isSelected,
        },
      )}
      onClick={() => {
        handleSelectCompany(company.id);
      }}
    >
      <Box className="grow flex justify-center items-center">
        <span className="text-base px-8">
          <a
            href={`/company/${company.id}`}
            className="hover:underline"
            target="_blank"
            onClick={(e) => {
              e.stopPropagation();
            }}
            rel="noreferrer"
          >
            {company.name}
          </a>
        </span>
      </Box>
      <Box className="w-20 flex justify-center items-center">
        {isSelected ? (
          <MdCheckCircle size={20} className="bg-primary-1 text-white" />
        ) : (
          <MdControlPoint size={20} className="text-primary" />
        )}
      </Box>
    </Box>
  );
};
